/*
 * ============================================================================
 * ExportMenuButton — bouton d'export standard des tables iSAL
 * ----------------------------------------------------------------------------
 * POSITIONNEMENT DANS UNE TABLE :
 *   Se place dans la barre d'outils du conteneur Paper de la table
 *   (MuiPaper-elevation2), cote DROIT, AU-DESSOUS de la TableSearchInput.
 *   Convention iSAL :
 *     - colonne droite de la toolbar : recherche en haut, puis une ligne
 *       avec le compteur de lignes + ce bouton
 *     - a gauche : filtres Select (stack vertical) ou titre de la table
 *   Layout type :
 *     <Stack direction="column" alignItems="flex-end">
 *         <TableSearchInput />
 *         <Stack direction="row" spacing={1} alignItems="center">
 *             <Typography>{total} ligne(s)</Typography>
 *             <ExportMenuButton />        <- ce composant
 *         </Stack>
 *     </Stack>
 *
 * DESIGN UI :
 *   - Button antd type="primary" (fond bleu #2A6596), icone DownloadOutlined,
 *     texte blanc force (style inline, les CSS globaux .ant-btn-primary et
 *     button { color: var(--blue) } rendraient sinon le texte invisible)
 *   - size="middle" dans les tables (prop size personnalisable)
 *   - Clic : ouvre un menu INLINE rendu en position:absolute sous le bouton
 *     (top: calc(100% + 4px), right: 0) — fiable quel que soit le scroll ou
 *     le conteneur, contrairement au Dropdown antd (rc-trigger)
 *   - Menu : 4 formats par defaut (PDF/Excel/Word/CSV) avec icones colorees ;
 *     la liste est personnalisable via la prop formats
 *   - ClickAwayListener : clic ailleurs -> ferme le menu
 *   - disabled : bouton grise, menu non ouvrable
 *
 * USAGE :
 *   <ExportMenuButton onExport={(fmt) => handleExport(fmt)}
 *                     disabled={total === 0} size="middle" />
 *   fmt recu : 'pdf' | 'xlsx' | 'docx' | 'csv' — telecharger ensuite via
 *   downloadBlob() de Services/export.ts.
 * ============================================================================
 */
import React, { useState } from 'react';
import { Button } from 'antd';
import { Box, ClickAwayListener } from '@mui/material';
import { DownloadOutlined, FilePdfOutlined, FileExcelOutlined, FileWordOutlined, FileTextOutlined } from '@ant-design/icons';

export interface ExportFormatItem {
    key: string;
    label: string;
    icon: React.ReactNode;
}

const DEFAULT_FORMATS: ExportFormatItem[] = [
    { key: 'pdf', label: 'PDF', icon: <FilePdfOutlined style={{ color: '#d32f2f' }} /> },
    { key: 'xlsx', label: 'Excel', icon: <FileExcelOutlined style={{ color: '#107C41' }} /> },
    { key: 'docx', label: 'Word', icon: <FileWordOutlined style={{ color: '#2B579A' }} /> },
    { key: 'csv', label: 'CSV', icon: <FileTextOutlined style={{ color: '#faad14' }} /> }
];

export interface ExportMenuButtonProps {
    onExport: (format: string) => void;
    disabled?: boolean;
    label?: string;
    size?: 'small' | 'middle' | 'large';
    formats?: ExportFormatItem[];
    buttonStyle?: React.CSSProperties;
    menuStyle?: React.CSSProperties;
}

const ExportMenuButton: React.FC<ExportMenuButtonProps> = ({
    onExport,
    disabled = false,
    label = 'Exporter',
    size = 'small',
    formats = DEFAULT_FORMATS,
    buttonStyle,
    menuStyle
}) => {
    const [open, setOpen] = useState(false);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Button
                    type='primary'
                    size={size}
                    icon={<DownloadOutlined />}
                    style={{ color: '#fff', ...buttonStyle }}
                    disabled={disabled}
                    onClick={() => setOpen(v => !v)}
                >
                    {label}
                </Button>
                {open && !disabled && (
                    <Box sx={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        zIndex: 1300,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        minWidth: 150,
                        py: 0.5,
                        boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
                        ...menuStyle
                    }}>
                        {formats.map(item => (
                            <Box
                                key={item.key}
                                onClick={() => { setOpen(false); onExport(item.key); }}
                                sx={{
                                    px: 1.5,
                                    py: 0.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: '0.82rem',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                            >
                                {item.icon} {item.label}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default ExportMenuButton;
